import { motion } from "framer-motion";
import { useEffect, useState, type ReactNode } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import yamperLogo from "@/assets/yamper-grow-logo.webp";

const brandColors = ["#E92052", "#602d90", "#fda700", "#08214A"];

const metricsKeys = [
  { value: "+250", labelKey: "hero.metric1" },
  { value: "3", labelKey: "hero.metric2" },
  { value: "360°", labelKey: "hero.metric3" },
  { value: "+11", labelKey: "hero.metric4" },
];

type HeroWordProps = {
  children: ReactNode;
  delay: number;
  className?: string;
  gradient?: boolean;
  colorIdx?: number;
  doorsOpen?: boolean;
};

const HeroWord = ({
  children,
  delay,
  className = "",
  gradient = false,
  colorIdx = 0,
  doorsOpen = true,
}: HeroWordProps) => (
  <motion.span
    className={`inline-block align-top py-[0.08em] px-[20px] ${className}`}
    initial={{ opacity: 0, y: 26, filter: "blur(10px)" }}
    animate={doorsOpen ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
    transition={{ duration: 0.82, delay, ease: [0.22, 1, 0.36, 1] }}
    style={
      gradient
        ? {
            backgroundImage: `linear-gradient(90deg, ${brandColors[colorIdx]}, ${brandColors[(colorIdx + 1) % brandColors.length]})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }
        : undefined
    }
  >
    {children}
  </motion.span>
);

const HeroSection = () => {
  const [doorsOpen, setDoorsOpen] = useState(false);
  const [doorsGone, setDoorsGone] = useState(false);
  const [colorIdx, setColorIdx] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setDoorsOpen(true), 2200);
    const removeTimer = setTimeout(() => setDoorsGone(true), 4200);

    return () => {
      clearTimeout(timer);
      clearTimeout(removeTimer);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setColorIdx((prev) => (prev + 1) % brandColors.length), 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {!doorsGone && (
        <motion.div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-foreground"
          initial={{ opacity: 1 }}
          animate={doorsOpen ? { opacity: 0 } : { opacity: 1 }}
          transition={{ duration: 1.8, ease: "easeInOut" }}
        >
          <h1 className="sr-only">Yamper Grow - Growth Marketing Agency</h1>
          <motion.img
            src={yamperLogo}
            alt="Yamper Grow"
            width={400}
            height={209}
            className="h-auto w-[280px] md:w-[400px]"
            fetchPriority="high"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={
              doorsOpen
                ? { opacity: 0, scale: 1.15, filter: "blur(20px)" }
                : { opacity: 1, scale: 1, filter: "blur(0px)" }
            }
            transition={
              doorsOpen
                ? { duration: 1.5, ease: "easeIn" }
                : { duration: 1, delay: 0.3, ease: "easeOut" }
            }
          />
        </motion.div>
      )}

      <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-foreground px-5 pt-28 pb-16 text-center text-white sm:px-6 md:pt-32">
        <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center">
          <motion.div className="mb-8 overflow-hidden">
            <motion.p
              className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/60 sm:text-xs sm:tracking-[0.4em]"
              initial={{ y: "100%", opacity: 0 }}
              animate={doorsOpen ? { y: "0%", opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              Growth Marketing Agency
            </motion.p>
          </motion.div>

          <h1 className="mx-auto pb-[0.16em] text-[2.15rem] font-black tracking-[-0.04em] leading-[1.1] sm:text-[2.6rem] md:text-[4.5rem] md:leading-[1.08] lg:text-[5.8rem] lg:leading-[1.06]">
              <span className="block">
                <HeroWord delay={0.1} doorsOpen={doorsOpen}>{t("hero.line1")}</HeroWord>
              </span>

              <span className="block pt-[0.04em] sm:whitespace-nowrap">
                <HeroWord delay={0.2} doorsOpen={doorsOpen}>{t("hero.line2")}{" "}</HeroWord>
                <motion.span
                  className="inline-block align-top py-[0.08em]"
                  initial={{ opacity: 0, y: 26, filter: "blur(10px)" }}
                  animate={doorsOpen ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
                  transition={{ duration: 0.82, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.span
                    className="inline-block"
                    animate={{
                      backgroundImage: `linear-gradient(90deg, ${brandColors[colorIdx]}, ${brandColors[(colorIdx + 1) % brandColors.length]})`,
                    }}
                    transition={{ duration: 1.8 }}
                    style={{
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Growth{t("hero.line3")}
                  </motion.span>
                </motion.span>
              </span>
            </h1>

          <motion.p
            className="mt-7 max-w-[22rem] px-1 text-center text-[0.95rem] leading-[1.45] text-white/60 text-balance sm:max-w-[34rem] sm:text-base md:mt-8 md:max-w-2xl md:text-xl md:leading-relaxed"
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            animate={doorsOpen ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {t("hero.desc")}
          </motion.p>

          <motion.a
            href="#contacto"
            className="mt-10 w-full max-w-[20.5rem] rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-foreground transition-colors hover:bg-brand-rosa hover:text-white sm:w-auto sm:max-w-none sm:px-10 sm:tracking-[0.15em]"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={doorsOpen ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.8, type: "spring", stiffness: 200 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#contacto");
              if (el) {
                const y = el.getBoundingClientRect().top + window.scrollY - 60;
                window.scrollTo({ top: y, behavior: "smooth" });
              }
            }}
          >
            {t("hero.cta")}
          </motion.a>

          <motion.div
            className="mt-16 grid w-full grid-cols-2 justify-center gap-x-8 gap-y-8 border-t border-white/10 pt-8 md:mt-20 md:flex md:items-center md:gap-16 md:pt-10"
            initial={{ opacity: 0 }}
            animate={doorsOpen ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            {metricsKeys.map((metric, index) => (
              <motion.div
                key={index}
                className="min-w-0 text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={doorsOpen ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 1.1 + index * 0.1 }}
              >
                <p className="text-[2rem] font-black tracking-tight md:text-4xl">{metric.value}</p>
                <p className="mt-1 whitespace-pre-line text-[10px] uppercase tracking-[0.2em] text-white/30 md:text-xs text-center">
                  {t(metric.labelKey)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
